import React from "react";
import { Plug, BookOpen, Newspaper, Building2, FileText } from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card, Badge } from "../../components/ui/Primitives";
import { PageHero, CTASection } from "../../components/marketing/Shared";

// Lightweight content pages for secondary routes (integrations, docs, blog,
// about, legal). Real copy would live in a CMS; here we show a coherent shell.
const PAGES = {
  integrations: {
    icon: Plug,
    breadcrumb: "nav.integrations",
    title: "Integrations",
    lead: "Connect SecProctoring to the tools your exams and events already run on.",
    groups: [
      { title: "LMS", items: ["Moodle", "Canvas", "Blackboard"] },
      { title: "SSO / Identity", items: ["SAML 2.0", "OAuth / OIDC", "Azure AD", "Okta"] },
      { title: "CTF platforms", items: ["CTFd", "rCTF", "Custom webhook"] },
      { title: "Broadcast", items: ["OBS", "NDI", "SRT / RTSP", "MediaMTX"] },
      { title: "Automation", items: ["Webhooks", "REST API", "Calendars"] },
    ],
  },
  docs: {
    icon: BookOpen,
    breadcrumb: "nav.docs",
    title: "Documentation & API",
    lead: "Quickstarts, REST API reference and webhook guides to integrate in minutes.",
    groups: [
      { title: "Getting started", items: ["Quickstart", "Authentication", "Concepts"] },
      { title: "REST API", items: ["Sessions", "Exams / Events", "Recordings", "Reports"] },
      { title: "Webhooks", items: ["Event types", "Signatures", "/recording/integration/position"] },
    ],
  },
  blog: {
    icon: Newspaper,
    breadcrumb: "nav.blog",
    title: "Blog & Resources",
    lead: "Articles, case studies and whitepapers on integrity at scale.",
    groups: [
      { title: "Latest", items: ["Scaling to 400 simultaneous sessions", "Why server timestamps matter", "Anti-cheat for CTF, done right"] },
    ],
  },
  about: {
    icon: Building2,
    breadcrumb: "footer.about",
    title: "About SecProctoring",
    lead: "A sec4us company building verifiable integrity for high-stakes assessments and competitions.",
    groups: [{ title: "Company", items: ["Mission", "Team", "Careers", "Press"] }],
  },
  privacy: { icon: FileText, breadcrumb: "footer.privacy", title: "Privacy Policy", lead: "How we collect, process and protect personal data (LGPD & GDPR).", legal: true },
  terms: { icon: FileText, breadcrumb: "footer.terms", title: "Terms of Service", lead: "The terms governing your use of SecProctoring.", legal: true },
  dpa: { icon: FileText, breadcrumb: "footer.dpa", title: "Data Processing Agreement", lead: "Roles, sub-processors, security measures and data-subject rights.", legal: true },
};

export default function SimplePage({ pageKey }) {
  const { t } = useI18n();
  const page = PAGES[pageKey] || PAGES.about;

  return (
    <>
      <PageHero breadcrumb={t(page.breadcrumb)} eyebrow={t(page.breadcrumb)} title={page.title} lead={page.lead} />
      <Section>
        <Container>
          {page.legal ? (
            <Card className="mx-auto max-w-3xl p-8">
              <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p>This is a demonstration document for the SecProctoring site build. In production, the full legal text would be maintained by legal counsel and versioned.</p>
                <p>Monitored users always receive a clear recording notice. You control retention windows, data export and right-to-erasure requests from the admin console.</p>
                <p>For a signed copy or custom terms, contact our team.</p>
              </div>
            </Card>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {page.groups.map((g) => (
                <Card key={g.title} className="p-6">
                  <h3 className="text-base font-bold">{g.title}</h3>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {g.items.map((it) => (
                      <li key={it}>
                        <Badge tone="neutral">{it}</Badge>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          )}
        </Container>
      </Section>
      <CTASection />
    </>
  );
}
